import React from 'react'
import { Check, ShieldCheck, Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { viewsLabel } from '@adthlete/common/utils/dealPricing'

export type DealPerformance = {
  terms: { usageDays: number; bonuses: { views: number; amount: number }[] } | null
  views: number
  posted: boolean
  bonus: {
    unlocked: { views: number; amount: number }[]
    total: number
    next: { views: number; amount: number } | null
    max: number
  } | null
  usage: {
    includedDays: number
    endsAt: string | null
    daysLeft: number | null
    extensions: unknown[]
  }
}

const money = (n: number) => `$${n.toLocaleString('en-US')}`
const compact = (n: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n)

/**
 * A gifting deal's bonuses for the athlete: what their post has earned as it passed each view
 * milestone, what the next one pays, and how long the brand can run it as an ad.
 */
export const GiftingBonusCard: React.FC<{ performance: DealPerformance }> = ({
  performance: p,
}) => {
  if (!p.terms) return null
  const unlocked = new Set(p.bonus?.unlocked.map((t) => t.views))
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <Trophy className="h-4 w-4 text-[#F9DA4C]" /> Your bonuses
        </p>
        <p className="text-sm font-bold tabular-nums text-[#4d7c0f]">
          {money(p.bonus?.total ?? 0)} earned
        </p>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {p.posted
          ? p.bonus?.next
            ? `${compact(p.views)} views. ${compact(p.bonus.next.views - p.views)} more unlocks ${money(p.bonus.next.amount)}.`
            : `${compact(p.views)} views. You've unlocked every bonus.`
          : `Post it and earn up to ${money(p.bonus?.max ?? 0)} as the views come in.`}
      </p>
      <ol className="mt-3 grid grid-cols-3 gap-2">
        {p.terms.bonuses.map((t) => {
          const done = unlocked.has(t.views)
          return (
            <li
              key={t.views}
              className={cn(
                'rounded-xl border px-2 py-2 text-center',
                done ? 'border-[#C0FD72]/60 bg-[#C0FD72]/10' : 'border-foreground/10',
              )}
            >
              <span className="block text-xs text-muted-foreground">
                {viewsLabel(t.views)} views
              </span>
              <span className="flex items-center justify-center gap-1 font-bold tabular-nums text-foreground">
                {done ? <Check className="h-3.5 w-3.5 text-[#4d7c0f]" strokeWidth={3} /> : null}
                {money(t.amount)}
              </span>
            </li>
          )
        })}
      </ol>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" />
        {p.usage.daysLeft !== null
          ? `The brand can run your post as an ad for ${p.usage.daysLeft} more days.`
          : `Includes ${p.terms.usageDays} days of paid usage once your post is live.`}
      </p>
    </div>
  )
}

export default GiftingBonusCard
